import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import qs from 'query-string';
import {
	Modal,
	Tabs,
	Tag,
	Divider,
	message,
	Typography,
	Breadcrumb,
	Button,
	Input,
	Switch,
	Select,
	Alert,
	Avatar,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import Form from '../../../../components/Form';
import Scripts from '../../../../components/ProgramDetailsTabs/Scripts';
import Vignettes from '../../../../components/ProgramDetailsTabs/Vignettes';
import CustomElements from '../../../../components/ProgramDetailsTabs/CustomElements';
import Segments from '../../../../components/ProgramDetailsTabs/Segments';
import AdsElements from '../../../../components/ProgramDetailsTabs/AdsElements';
import RecordedOffs from '../../../../components/ProgramDetailsTabs/RecordedOffs';
import LocalNews from '../../../../components/ProgramDetailsTabs/LocalNews';
import LocalOffs from '../../../../components/ProgramDetailsTabs/LocalOffs';
import LocalBlitz from '../../../../components/ProgramDetailsTabs/LocalBlitz';
import LocalWpps from '../../../../components/ProgramDetailsTabs/LocalWpps';
import LocalInterviews from '../../../../components/ProgramDetailsTabs/LocalInterviews';
import NightlyGenerationSchedule from '../../../../components/ProgramDetailsTabs/NightlyGenerationSchedule';
import { checkResource, checkResourcesGroup } from '../../../../helpers/checkResource';

import { Container } from './styles';

import GenresAPI from '../../../../services/sdks/genres';
import ProgramsAPI from '../../../../services/sdks/programs';
import ShareAPI from '../../../../services/sdks/share';
import CastersAPI from '../../../../services/sdks/caster';

import {
	FiFileText,
	FiList,
	FiStopCircle,
	FiGlobe,
	FiMic,
	FiVoicemail,
	FiDollarSign,
	FiEdit,
	FiTrash2,
	FiBook,
	FiCalendar,
	FiXCircle,
	FiSave,
	FiZap,
	FiUser,
	FiPlay,
	FiCpu,
} from 'react-icons/fi';
import { AiOutlineWhatsApp } from 'react-icons/ai';
import TKPlayOffs from '../../../../components/ProgramDetailsTabs/TKPlayOffs';
import NightlyGenerationSchedulesAPI from '../../../../services/sdks/nightly-generation-schedules';
import userTypes from '../../../../constants/userTypes';
import resourcesKeys from '../../../../constants/resourcesKeys';
import resourceGroupsKeys from '../../../../constants/resourceGroupsKeys';
import { CasterOptContainer } from '../CreateProgram/styles';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import env from '../../../../config/env';
import AiModelsAPI from '../../../../services/sdks/ai-models';
import AiModels from '../../../../components/ProgramDetailsTabs/AiModels';

const RuleInput = ({ ruleKey, label, accountInfos, handleChangeAccountInfo, placeholderText }) => {
	const [inputValue, setInputValue] = React.useState('');

	const handleAddRule = (e) => {
		e.preventDefault();
		const value = inputValue.trim();
		if (!value) return;

		const updated = {
			...accountInfos.rules,
			[ruleKey]: [...(accountInfos.rules?.[ruleKey] || []), value],
		};

		handleChangeAccountInfo('rules', updated);

		setInputValue('');
	};

	const handleRemoveRule = (ruleToRemove) => {
		const updated = {
			...accountInfos.rules,
			[ruleKey]: accountInfos.rules[ruleKey].filter((rule) => rule !== ruleToRemove),
		};

		handleChangeAccountInfo('rules', updated);
	};

	return (
		<Form.Item
			label={label}
			labelCol={{ span: 24 }}
			wrapperCol={{ span: 24 }}
			style={{ marginBottom: 0 }}>
			<>
				<Input.TextArea
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					placeholder={placeholderText}
					autoSize={{ minRows: 1, maxRows: 3 }}
					onPressEnter={handleAddRule}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
						marginTop: 8,
					}}>
					{(accountInfos.rules?.[ruleKey] || []).map((rule, index) => (
						<Tag
							key={`${ruleKey}-${rule}-${index}`}
							closable
							onClose={(e) => {
								e.preventDefault();
								handleRemoveRule(rule);
							}}
							style={{
								whiteSpace: 'normal',
								lineHeight: 1.5,
								padding: '8px 12px',
								fontSize: 14,
								background: '#fafafa',
								border: '1px solid #d9d9d9',
							}}>
							{rule}
						</Tag>
					))}
				</div>
			</>
		</Form.Item>
	);
};

const ProgramDetails = ({ user }) => {
	const { search } = useLocation();
	const { programId } = useParams();
	const history = useHistory();

	const [fallback, setFallback] = useState({ initialData: true });
	const [genres, setGenres] = useState([]);
	const [casters, setCasters] = useState([]);
	const [program, setProgram] = useState(null);
	const [updatedProgram, setUpdatedProgram] = useState(program);
	const [scripts, setScripts] = useState(null);
	const [visibleModals, setVisibleModals] = useState({ edit: false });
	const [deleteReason, setDeleteReason] = useState('');
	const [sharing, setSharing] = useState(null);
	const [nightlyGenerationSchedule, setNightlyGenerationSchedule] = useState(null);

	/** elements */
	const [elements, setElements] = useState(null);
	const [customElements, setCustomElements] = useState(null);
	const [segments, setSegments] = useState(null);

	const [aiModels, setAiModels] = useState([]);
	const [selectedAiModels, setSelectedAiModels] = useState([]);

	const getAiModels = async (userId) => {
		setAiModels([]);
		const { data } = await AiModelsAPI.listAiModels({ userId, pageSize: 9999 });
		if (data.total) {
			const aiModelsValues = data.aiModels.map((aiModel) => {
				return { value: aiModel._id, label: aiModel.title };
			});
			setAiModels(aiModelsValues);
		} else {
			setAiModels([]);
		}
	};

	const getSelectedAiModels = async (programId) => {
		setSelectedAiModels([]);
		const { data } = await ProgramsAPI.show(programId);
		const { program } = data;
		if (program?.aiModels.length) {
			const selectedIds = (program?.aiModels ?? []).map((aiModel) => aiModel?._id);
			setSelectedAiModels(selectedIds);
		}
	};

	useEffect(() => {
		getAiModels(user._id);
		getSelectedAiModels(programId);
	}, [programId, user._id]);

	const isSharedProgram = useMemo(() => {
		if (!user || !program) {
			return undefined;
		}

		return program?.userId?._id !== user?._id;
	}, [user, program]);

	const showTalkManagedElements = useMemo(() => {
		return [userTypes.ADMIN, userTypes.TALK].includes(user?.type) && !isSharedProgram;
	}, [user, isSharedProgram]);

	useEffect(() => {
		const fetchNightlyGenerationSchedule = async () => {
			setFallback((prev) => ({ ...prev, fetchingNightlyGenerationSchedule: true }));

			try {
				const res = await NightlyGenerationSchedulesAPI.getByProgramId(programId);
				const { weekSchedule, ...rest } = res.data.nightlyGenerationSchedule;

				setNightlyGenerationSchedule({
					...rest,
					weekSchedule: weekSchedule.sort((a, b) => (a.day > b.day ? 1 : -1)),
				});
			} catch (error) {
				if (error.status === 404) {
					setNightlyGenerationSchedule(null);
				} else {
					message.error('Houve um erro ao buscar o cronograma de geração noturna');
				}
			}

			setFallback((prev) => ({ ...prev, fetchingNightlyGenerationSchedule: false }));
		};

		const fetchInitialData = async () => {
			try {
				const {
					data: { program },
				} = await ProgramsAPI.show(programId);
				const {
					data: { genres },
				} = await GenresAPI.index();

				const {
					data: { casters },
				} = await CastersAPI.index(`userId=${user._id}&page=0&limit=999`);

				setGenres(genres);
				setCasters(casters);
				setProgram({
					...program,
					theme: program.theme || '',
					audience: program.audience || '',
					caster: program.caster?._id || undefined,
				});

				const isSharedProgram = program?.userId?._id !== user?._id;

				if (isSharedProgram) {
					const {
						data: { sharings },
					} = await ShareAPI.index(`user=${user?._id}&program=${program._id}`);

					if (sharings) {
						setSharing(sharings[0]);
					}
				}

				return setFallback((prev) => ({ ...prev, initialData: false }));
			} catch (error) {
				console.error(error);
			}
		};

		if (user && programId) {
			fetchInitialData();
			fetchNightlyGenerationSchedule();
		}
	}, [programId, user]);

	useEffect(() => {
		setUpdatedProgram({
			name: program?.name,
			description: program?.description,
			filenameTemplate: program?.filenameTemplate,
			genreId: program?.genreId?._id,
			auto: program?.auto,
			caster: program?.caster,
			theme: program?.theme,
			audience: program?.audience,
			dayPeriod: program?.dayPeriod,
			personality: program?.personality,
			accent: program?.accent,
			regionalCharacteristics: program?.regionalCharacteristics,
			rules: program?.rules,
			segments: program?.segments,
		});
	}, [program]);

	const handleUpdateAuto = useCallback(
		async (auto) => {
			try {
				setFallback((prev) => ({ ...prev, updatingNocturnalGeneration: true }));

				if (isSharedProgram) {
					await ShareAPI.modify(sharing._id, { auto });
					setSharing((prev) => ({ ...prev, auto }));
				} else {
					await ProgramsAPI.update(programId, { auto });
					setProgram((prev) => ({ ...prev, auto }));
				}

				setFallback((prev) => ({ ...prev, updatingNocturnalGeneration: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao atualizar a geração noturna');
			}
		},
		[isSharedProgram, sharing, programId]
	);

	const handleUpdateInfos = useCallback(
		async (selectedAiModelsProp) => {
			try {
				const payload = updatedProgram;
				const validationSchema = Yup.object().shape({
					name: Yup.string().required('Informe o nome'),
					description: Yup.string().required('Informe a descrição'),
					theme: Yup.string().required('Informe o tema'),
					audience: Yup.string().required('Informe o público alvo'),
					filenameTemplate: Yup.string().required('Informe o nome dos arquivos'),
					genreId: Yup.string().required('Informe o gênero'),
					dayPeriod: Yup.string()
						.nullable()
						.notRequired()
						.oneOf(
							[
								'MORNING',
								'LATE_MORNING',
								'AFTERNOON',
								'LATE_AFTERNOON',
								'NIGHT',
								'LATE_NIGHT',
								'DAWN',
								null,
							],
							'dayPeriod inválido'
						),
				});

				await validationSchema.validate(payload);

				setFallback((prev) => ({ ...prev, updating: true }));

				await ProgramsAPI.update(programId, { ...payload, aiModelIds: selectedAiModelsProp });

				return message.success('Informações editadas com sucesso!', 4, () => {
					window.location.reload();
				});
			} catch (error) {
				if (error instanceof Yup.ValidationError) {
					return message.error(`${error.message} do programa`);
				}

				console.error(error);
			}
		},
		[updatedProgram, programId]
	);

	const handleRequestProgramExclusion = useCallback(async () => {
		try {
			if (!deleteReason) {
				return message.warn('Por favor, informe o motivo');
			}

			setFallback((prev) => ({ ...prev, deleting: true }));

			await ProgramsAPI.requestExclusion(program._id, deleteReason);

			setDeleteReason('');
			setProgram((prev) => ({ ...prev, deleteReason, deleteRequested: true }));
			setFallback((prev) => ({ ...prev, deleting: false }));
			setVisibleModals((prev) => ({ ...prev, delete: false }));

			return message.success('A exclusão foi enviada para análise');
		} catch (error) {
			console.error(error);
		}
	}, [program, deleteReason]);

	const handleCancelProgramExclusion = useCallback(async () => {
		try {
			setFallback((prev) => ({ ...prev, exclusionRequest: true }));

			await ProgramsAPI.cancelExclusionRequest(program._id);

			setProgram((prev) => ({ ...prev, deleteReason: null, deleteRequested: false }));
			setFallback((prev) => ({ ...prev, exclusionRequest: false }));

			return message.success('A exclusão foi cancelada');
		} catch (error) {
			console.error(error);
		}
	}, [program]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title={program?.name || ''} />

			<Container>
				<Breadcrumb
					separator='>'
					style={{ marginBottom: 24 }}
					routes={[
						{ breadcrumbName: 'INÍCIO' },
						{ breadcrumbName: 'CONTEÚDOS' },
						{ breadcrumbName: 'GERENCIAR PROGRAMAS' },
						{ breadcrumbName: 'DETALHES' },
					]}
				/>
				<header>
					<div className='title'>
						<Typography.Title level={2}>{program?.name}</Typography.Title>

						{!program?.isEditable && (
							<Tag color='red' style={{ marginLeft: 12 }}>
								Programa não editável
							</Tag>
						)}

						{isSharedProgram && (
							<Tag color='gold' style={{ marginLeft: 12 }}>
								Programa compartilhado
							</Tag>
						)}
					</div>
					<div className='buttons'>
						<Button
							type='primary'
							onClick={() => setVisibleModals({ ...visibleModals, edit: true })}
							disabled={isSharedProgram || !program?.isEditable}>
							<FiEdit /> Editar
						</Button>
						<Button
							type='danger'
							onClick={() => setVisibleModals({ ...visibleModals, delete: true })}
							disabled={isSharedProgram || program?.deleteRequested}>
							<FiTrash2 /> Excluir
						</Button>
					</div>
				</header>

				<div className='infos'>
					<div>
						<FiBook /> {program?.genreId?.name}
					</div>
					<div>
						<FiCalendar /> Criado em {format(parseISO(program?.dateUpload), 'dd/MM/yyyy')}
					</div>

					{checkResource(user, resourcesKeys.NOCTURNAL_GENERATION) && (
						<div>
							<Form.Item label='Geração noturna'>
								<Switch
									disabled={fallback?.updatingNocturnalGeneration}
									style={{ width: 'max-content' }}
									checked={isSharedProgram ? sharing.auto : program.auto}
									onChange={(value) => handleUpdateAuto(value)}
								/>
							</Form.Item>
						</div>
					)}
				</div>

				{program?.deleteRequested && (
					<Alert
						showIcon
						type='error'
						message={
							<div
								style={{
									width: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}>
								<span>Exclusão requisitada!</span>
								<Button size='small' type='danger' onClick={handleCancelProgramExclusion}>
									Cancelar
								</Button>
							</div>
						}
						description={`➤ ${program?.deleteReason}`}
						style={{ width: '100%', margin: '24px 0' }}
					/>
				)}

				<Divider />

				<Tabs
					activeKey={qs.parse(search).tab || 'scripts'}
					tabPosition='left'
					onChange={(tab) => history.push(`${history.location.pathname}?tab=${tab}`)}>
					<Tabs.TabPane
						key='scripts'
						tab={
							<>
								<FiFileText />
								Modelos
							</>
						}>
						<Scripts data={scripts} setData={setScripts} program={program} user={user} />
					</Tabs.TabPane>

					{!isSharedProgram && program.auto && (
						<Tabs.TabPane
							key='nightly-generation-schedule'
							tab={
								<>
									<FiCalendar />
									Geração Noturna
								</>
							}>
							<NightlyGenerationSchedule
								data={nightlyGenerationSchedule}
								setData={setNightlyGenerationSchedule}
								program={program}
							/>
						</Tabs.TabPane>
					)}

					{!isSharedProgram && checkResource(user, resourcesKeys.VIGNETTES_PROGRAM) && (
						<Tabs.TabPane
							key='vignettes'
							tab={
								<>
									<FiList />
									Vinhetas
								</>
							}>
							<Vignettes
								data={elements}
								setData={setElements}
								fallback={fallback}
								program={program}
								user={user}
							/>
						</Tabs.TabPane>
					)}

					{!isSharedProgram && (
						<Tabs.TabPane
							key='segments'
							tab={
								<>
									<FiStopCircle />
									Quadros
								</>
							}>
							<Segments data={segments} setData={setSegments} program={program} user={user} />
						</Tabs.TabPane>
					)}

					{!isSharedProgram && checkResource(user, resourcesKeys.CUSTOMS) && (
						<Tabs.TabPane
							key='custom-elements'
							tab={
								<>
									<FiStopCircle />
									Customizados
								</>
							}>
							<CustomElements
								data={customElements}
								setData={setCustomElements}
								program={program}
								user={user}
							/>
						</Tabs.TabPane>
					)}

					{!isSharedProgram && (
						<Tabs.TabPane
							key='ai-models'
							tab={
								<>
									<FiCpu />
									Modelos de IA
								</>
							}>
							<AiModels aiModels={program?.aiModels} programId={program._id} />
						</Tabs.TabPane>
					)}

					{checkResourcesGroup(user, resourceGroupsKeys.ADS) && (
						<Tabs.TabPane
							key='ads'
							tab={
								<>
									<FiDollarSign />
									Publicidades
								</>
							}>
							<AdsElements program={program} />
						</Tabs.TabPane>
					)}

					{!isSharedProgram && checkResource(user, resourcesKeys.OFFS_LOCAL) && (
						<Tabs.TabPane
							key='local-offs'
							tab={
								<>
									<FiMic />
									Offs Locais
								</>
							}>
							<LocalOffs program={program} />
						</Tabs.TabPane>
					)}

					{showTalkManagedElements && (
						<Tabs.TabPane
							key='recorded-offs'
							tab={
								<>
									<FiVoicemail />
									TKVOX
								</>
							}>
							<RecordedOffs program={program} />
						</Tabs.TabPane>
					)}

					{showTalkManagedElements && (
						<Tabs.TabPane
							key='tk-play'
							tab={
								<>
									<FiPlay />
									TKPLAY
								</>
							}>
							<TKPlayOffs program={program} />
						</Tabs.TabPane>
					)}

					{checkResource(user, resourcesKeys.NEWS_LOCAL) && (
						<Tabs.TabPane
							key='local-news'
							tab={
								<>
									<FiGlobe />
									Notícias locais
								</>
							}>
							<LocalNews program={program} />
						</Tabs.TabPane>
					)}

					{checkResource(user, resourcesKeys.BLITZ_LOCAL) && (
						<Tabs.TabPane
							key='local-blitz'
							tab={
								<>
									<FiZap />
									Blitz locais
								</>
							}>
							<LocalBlitz program={program} />
						</Tabs.TabPane>
					)}

					{checkResource(user, resourcesKeys.INTERVIEW_LOCAL) && (
						<Tabs.TabPane
							key='local-interviews'
							tab={
								<>
									<FiUser />
									Entrevistas locais
								</>
							}>
							<LocalInterviews program={program} />
						</Tabs.TabPane>
					)}

					{checkResource(user, resourcesKeys.WPP_LOCAL) && (
						<Tabs.TabPane
							key='local-wpps'
							tab={
								<>
									<AiOutlineWhatsApp />
									Wpp's locais
								</>
							}>
							<LocalWpps program={program} />
						</Tabs.TabPane>
					)}
				</Tabs>
			</Container>

			<Modal
				visible={visibleModals?.edit}
				okButtonProps={{ loading: fallback?.updating }}
				cancelButtonProps={{ disabled: fallback?.updating }}
				onCancel={() => setVisibleModals({ ...visibleModals, edit: false })}
				onOk={() => handleUpdateInfos(selectedAiModels)}
				width='80vw'
				cancelText={
					<>
						<FiXCircle /> Cancelar
					</>
				}
				okText={
					<>
						<FiSave /> Atualizar
					</>
				}
				title={
					<>
						<FiEdit /> Editar informações{' '}
					</>
				}>
				<Form.Container layout={'1fr 1fr 1fr'}>
					<Form.Item label='Nome'>
						<Input
							style={{ height: '40px' }}
							value={updatedProgram?.name}
							onChange={({ target: { value } }) =>
								setUpdatedProgram({ ...updatedProgram, name: value })
							}
						/>
					</Form.Item>

					<Form.Item label='Locutor'>
						<Select
							showSearch
							size='large'
							value={updatedProgram.caster}
							onChange={(value) => setUpdatedProgram({ ...updatedProgram, caster: value })}
							placeholder='Selecione o locutor do programa'
							filterOption={(input, { props: { children } }) => {
								return children.props._search.toLowerCase().indexOf(input.toLowerCase()) >= 0;
							}}>
							{casters.map((caster) => (
								<Select.Option
									key={caster._id}
									value={caster._id}
									_search={`${caster.name} ${caster.surname}`}>
									<CasterOptContainer>
										<Avatar
											size='large'
											src={resolveFileSrc({
												fileName: caster.profilePic,
												folder: ['profile_pics'],
												bucket: env.miscS3Bucket,
											})}
										/>
										<span>
											{caster.name} {caster.surname}
										</span>
									</CasterOptContainer>
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Modelos de IA'>
						<Select
							mode='multiple'
							allowClear
							showSearch
							size='large'
							style={{ width: '100%' }}
							placeholder='Digite para buscar ou selecione'
							onChange={setSelectedAiModels}
							value={selectedAiModels}
							// loading={aiModels.length === 0}
							disabled={aiModels.length === 0}
							notFoundContent={aiModels.length === 0 ? 'Carregando...' : 'Nenhuma opção encontrada'}
							filterOption={(input, option) =>
								option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
							}>
							{aiModels.map((aiModel) => (
								<Select.Option key={aiModel.value} value={aiModel.value}>
									{aiModel.label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Tema do Programa'>
						<Input
							style={{ height: '40px' }}
							placeholder='Ex: Entretenimento e música pop'
							value={updatedProgram?.theme}
							onChange={({ target: { value } }) =>
								setUpdatedProgram({ ...updatedProgram, theme: value })
							}
						/>
					</Form.Item>
					<Form.Item label='Público Alvo'>
						<Input
							style={{ height: '40px' }}
							placeholder='Ex: Jovens adultos entre 18 e 35 anos'
							value={updatedProgram?.audience}
							onChange={({ target: { value } }) =>
								setUpdatedProgram({ ...updatedProgram, audience: value })
							}
						/>
					</Form.Item>
					<Form.Item label='Template de arquivo'>
						<Input
							style={{ height: '40px' }}
							value={updatedProgram?.filenameTemplate}
							onChange={({ target: { value } }) =>
								setUpdatedProgram({ ...updatedProgram, filenameTemplate: value })
							}
						/>
					</Form.Item>

					<Form.Item label='Gênero'>
						<Select
							size='large'
							value={updatedProgram?.genreId}
							onChange={(genreId) => setUpdatedProgram({ ...updatedProgram, genreId })}>
							{genres.map((g) => (
								<Select.Option key={g._id}>{g?.name}</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Período do Dia'>
						<Select
							allowClear
							size='large'
							placeholder='Selecione o período do dia do programa (opcional)'
							value={updatedProgram?.dayPeriod}
							onChange={(dayPeriod) => setUpdatedProgram({ ...updatedProgram, dayPeriod })}>
							<Select.Option value='MORNING'>Manhã</Select.Option>
							<Select.Option value='LATE_MORNING'>Final da Manhã</Select.Option>
							<Select.Option value='AFTERNOON'>Tarde</Select.Option>
							<Select.Option value='LATE_AFTERNOON'>Final da Tarde</Select.Option>
							<Select.Option value='NIGHT'>Noite</Select.Option>
							<Select.Option value='LATE_NIGHT'>Final da Noite</Select.Option>
							<Select.Option value='DAWN'>Madrugada</Select.Option>
						</Select>
					</Form.Item>
				</Form.Container>

				<Form.Item label='Descrição'>
					<Input.TextArea
						value={updatedProgram?.description}
						onChange={({ target: { value } }) =>
							setUpdatedProgram({ ...updatedProgram, description: value })
						}
					/>
				</Form.Item>

				<Form.Item optional size={2} label='Segmentos'>
					<Select
						mode='multiple'
						size='large'
						name='segments'
						placeholder='Selecione um ou mais segmentos'
						value={updatedProgram.segments || []}
						onChange={(value) => setUpdatedProgram({ ...updatedProgram, segments: value })}>
						<Select.Option key='POPULAR' value='POPULAR'>
							Popular
						</Select.Option>
						<Select.Option key='JOVEM' value='JOVEM'>
							Jovem
						</Select.Option>
						<Select.Option key='RELIGIOSO' value='RELIGIOSO'>
							Religioso
						</Select.Option>
						<Select.Option key='NEWS' value='NEWS'>
							News / Jornalismo
						</Select.Option>
						<Select.Option key='ESPORTIVA' value='ESPORTIVA'>
							Esportiva
						</Select.Option>
						<Select.Option key='EDUCATIVA' value='EDUCATIVA'>
							Educativa
						</Select.Option>
						<Select.Option key='UNIVERSITÁRIA' value='UNIVERSITÁRIA'>
							Universitária
						</Select.Option>
						<Select.Option key='COMUNITÁRIA' value='COMUNITÁRIA'>
							Comunitária
						</Select.Option>
						<Select.Option key='RETRO' value='RETRO'>
							Retro
						</Select.Option>
						<Select.Option key='REGIONAL' value='REGIONAL'>
							Regional
						</Select.Option>
						<Select.Option key='VARIEDADES' value='VARIEDADES'>
							Variedades
						</Select.Option>
						<Select.Option key='CORPORATIVA' value='CORPORATIVA'>
							Corporativa
						</Select.Option>
						<Select.Option key='GOVERNAMENTAL' value='GOVERNAMENTAL'>
							Governamental
						</Select.Option>
						<Select.Option key='INFANTIL' value='INFANTIL'>
							Infantil
						</Select.Option>
						<Select.Option key='HUMOR' value='HUMOR'>
							Humor
						</Select.Option>
						<Select.Option key='ENTRETENIMENTO' value='ENTRETENIMENTO'>
							Entretenimento
						</Select.Option>
						<Select.Option key='CULTURAL' value='CULTURAL'>
							Cultural
						</Select.Option>
						<Select.Option key='TALK' value='TALK'>
							Talk
						</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item label='Personalidade' style={{ marginTop: 16 }} optional>
					<Input.TextArea
						placeholder='Descreva a Personalidade'
						value={updatedProgram?.personality}
						onChange={({ target: { value } }) => {
							setUpdatedProgram({ ...updatedProgram, personality: value });
						}}
					/>
				</Form.Item>

				<Form.Item label='Sotaque' style={{ marginTop: 16 }} optional>
					<Input.TextArea
						placeholder='Descreva o Sotaque'
						value={updatedProgram?.accent}
						onChange={({ target: { value } }) => {
							setUpdatedProgram({ ...updatedProgram, accent: value });
						}}
					/>
				</Form.Item>

				<Form.Item label='Características Regionais' style={{ marginTop: 16 }} optional>
					<Input.TextArea
						placeholder='Descreva as Características Regionais'
						value={updatedProgram?.regionalCharacteristics}
						onChange={({ target: { value } }) => {
							setUpdatedProgram({ ...updatedProgram, regionalCharacteristics: value });
						}}
					/>
				</Form.Item>

				<Form.Item optional label='Regras' style={{ marginBottom: 0 }} size={6}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						{[
							{
								key: 'mandatory',
								label: 'Obrigatórias',
								placeholderText: 'Digite uma regra obrigatória e pressione Enter',
							},
							{
								key: 'forbidden',
								label: 'Proibidas',
								placeholderText: 'Digite uma regra proibida e pressione Enter',
							},
							{
								key: 'recommended',
								label: 'Recomendadas',
								placeholderText: 'Digite uma regra recomendada e pressione Enter',
							},
						].map(({ key, label, placeholderText }) => (
							<RuleInput
								key={key}
								ruleKey={key}
								label={label}
								placeholderText={placeholderText}
								accountInfos={updatedProgram}
								handleChangeAccountInfo={(key, value) =>
									setUpdatedProgram({ ...updatedProgram, [key]: value })
								}
							/>
						))}
					</div>
				</Form.Item>
			</Modal>

			<Modal
				visible={visibleModals?.delete}
				title={
					<>
						<FiTrash2 />
						Requisitar exclusão do programa
					</>
				}
				okText={
					<>
						<FiTrash2 /> Enviar requisição de exclusão
					</>
				}
				cancelText={
					<>
						<FiXCircle /> Cancelar
					</>
				}
				onOk={handleRequestProgramExclusion}
				onCancel={() => setVisibleModals({ ...visibleModals, delete: false })}
				okButtonProps={{ loading: fallback?.deleting }}
				cancelButtonProps={{ disabled: fallback?.deleting }}>
				<Typography.Text>
					Após requisitar a exclusão desse programa, seu pedido irá passar pela análise de nossos
					administradores e somente após aprovação, o mesmo será apagado.
				</Typography.Text>
				<Divider />

				<Typography.Paragraph>
					Por favor, informe o motivo da exclusão no campo abaixo:
				</Typography.Paragraph>

				<Input.TextArea
					value={deleteReason}
					rows={1}
					placeholder='Informe o motivo da exclusão'
					onChange={({ target: { value } }) => setDeleteReason(value)}
				/>
			</Modal>
		</>
	);
};

const mapStateToProps = (state) => ({ user: state.user });
export default connect(mapStateToProps)(ProgramDetails);
